import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { 
  Button, 
  buttonVariants
} from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { 
  Mail, 
  MessageSquare,
  BarChart, 
  Settings, 
  Link as LinkIcon, 
  ExternalLink, 
  Key, 
  Unlock, 
  Server, 
  Clock, 
  X, 
  Check, 
  RefreshCcw,
  Lock,
  Upload,
  Phone
} from "lucide-react";
import { testEmailConnection } from '@/utils/config/email';
import { EmailConfig } from '@/utils/config/types';
import { useToast } from '@/hooks/use-toast';

const IntegrationsPanel: React.FC = () => {
  const { toast } = useToast();
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [isZohoConnected, setIsZohoConnected] = useState(false);
  const [isOutlookConnected, setIsOutlookConnected] = useState(false);
  const [isTwilioConnected, setIsTwilioConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeEmailProvider, setActiveEmailProvider] = useState<'gmail' | 'zoho' | 'outlook' | null>(null);
  const [storeKey, setStoreKey] = useState('');

  const handleConnectEmail = async (provider: 'gmail' | 'zoho' | 'outlook') => {
    setIsConnecting(true);
    setActiveEmailProvider(provider);
    
    try {
      // Simulate API call to connect to email provider
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update state based on which provider is being connected
      switch (provider) {
        case 'gmail':
          setIsGmailConnected(true);
          break;
        case 'zoho':
          setIsZohoConnected(true);
          break;
        case 'outlook':
          setIsOutlookConnected(true);
          break;
      }
      
      toast({
        title: "Connected successfully",
        description: `Your ${provider.charAt(0).toUpperCase() + provider.slice(1)} account has been connected.`,
      });
    } catch (error) {
      toast({
        title: "Connection failed",
        description: `Failed to connect to ${provider}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
      setActiveEmailProvider(null);
    }
  };
  
  const handleDisconnectEmail = async (provider: 'gmail' | 'zoho' | 'outlook') => {
    setIsConnecting(true);
    setActiveEmailProvider(provider);
    
    try {
      // Simulate API call to disconnect from email provider
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update state based on which provider is being disconnected
      switch (provider) {
        case 'gmail':
          setIsGmailConnected(false);
          break;
        case 'zoho':
          setIsZohoConnected(false);
          break;
        case 'outlook':
          setIsOutlookConnected(false);
          break;
      }
      
      toast({
        title: "Disconnected successfully",
        description: `Your ${provider.charAt(0).toUpperCase() + provider.slice(1)} account has been disconnected.`,
      });
    } catch (error) {
      toast({
        title: "Disconnection failed",
        description: `Failed to disconnect from ${provider}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
      setActiveEmailProvider(null);
    }
  };

  const handleEditIntegration = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    setIsEditing(false);
    toast({
      title: "Integration Updated",
      description: "Your integration settings have been saved.",
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email Integration Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="p-2 rounded-full bg-blue-100">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <CardTitle>Email Integration</CardTitle>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleEditIntegration}
                disabled={isConnecting}
              >
                Edit
              </Button>
            </div>
            <CardDescription>
              Connect email providers to send and receive customer emails
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Store Key Input */}
            <div className="space-y-2">
              <Label htmlFor="store-key">Store Key (Optional)</Label>
              <Input
                id="store-key"
                value={storeKey}
                onChange={(e) => setStoreKey(e.target.value)}
                placeholder="Enter store key"
                disabled={!isEditing}
              />
              <p className="text-xs text-muted-foreground">
                Optional key for store-specific configurations
              </p>
            </div>

            {/* Gmail Integration */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-full">
                    <Mail className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Gmail</h3>
                    <p className="text-xs text-gray-500">Send and receive through Gmail</p>
                  </div>
                </div>
                <Switch 
                  checked={isGmailConnected} 
                  onCheckedChange={(checked) => {
                    if (checked) {
                      handleConnectEmail('gmail');
                    } else {
                      handleDisconnectEmail('gmail');
                    }
                  }}
                  disabled={isConnecting}
                />
              </div>
              
              {isGmailConnected ? (
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 border border-green-100 rounded-md flex items-center justify-between">
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium">Connected to Gmail</p>
                        <p className="text-xs text-gray-500">master@referme.site</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-1"
                      disabled={isConnecting && activeEmailProvider === 'gmail'}
                    >
                      {isConnecting && activeEmailProvider === 'gmail' ? (
                        <RefreshCcw className="h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCcw className="h-3 w-3" />
                      )}
                      Sync
                    </Button>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Last synced</span>
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1 text-gray-400" />
                      Just now
                    </span>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => handleConnectEmail('gmail')}
                  disabled={isConnecting}
                >
                  {isConnecting && activeEmailProvider === 'gmail' ? (
                    <><RefreshCcw className="h-4 w-4 mr-2 animate-spin" /> Connecting...</>
                  ) : (
                    <><Mail className="h-4 w-4 mr-2" /> Connect Gmail</>
                  )}
                </Button>
              )}
            </div>
            
            {/* Outlook Integration */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-50 rounded-full">
                    <Mail className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Outlook</h3>
                    <p className="text-xs text-gray-500">Send and receive through Microsoft Outlook</p>
                  </div>
                </div>
                <Switch 
                  checked={isOutlookConnected} 
                  onCheckedChange={(checked) => {
                    if (checked) {
                      handleConnectEmail('outlook');
                    } else {
                      handleDisconnectEmail('outlook');
                    }
                  }}
                  disabled={isConnecting}
                />
              </div>
              
              {isOutlookConnected ? (
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 border border-green-100 rounded-md flex items-center justify-between">
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium">Connected to Outlook</p>
                        <p className="text-xs text-gray-500">sales@referme.site</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-1"
                      disabled={isConnecting && activeEmailProvider === 'outlook'}
                    >
                      {isConnecting && activeEmailProvider === 'outlook' ? (
                        <RefreshCcw className="h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCcw className="h-3 w-3" />
                      )}
                      Sync
                    </Button>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Last synced</span>
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1 text-gray-400" />
                      Just now
                    </span>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => handleConnectEmail('outlook')}
                  disabled={isConnecting}
                >
                  {isConnecting && activeEmailProvider === 'outlook' ? (
                    <><RefreshCcw className="h-4 w-4 mr-2 animate-spin" /> Connecting...</>
                  ) : (
                    <><Mail className="h-4 w-4 mr-2" /> Connect Outlook</>
                  )}
                </Button>
              )}
            </div>
            
            {/* Zoho Integration */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-full">
                    <Mail className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Zoho Mail</h3>
                    <p className="text-xs text-gray-500">Send and receive through Zoho Mail</p>
                  </div>
                </div>
                <Switch 
                  checked={isZohoConnected} 
                  onCheckedChange={(checked) => {
                    if (checked) {
                      handleConnectEmail('zoho');
                    } else {
                      handleDisconnectEmail('zoho');
                    }
                  }}
                  disabled={isConnecting}
                />
              </div>
              
              {isZohoConnected ? (
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 border border-green-100 rounded-md flex items-center justify-between">
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-green-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium">Connected to Zoho Mail</p>
                        <p className="text-xs text-gray-500">support@referme.site</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-1"
                      disabled={isConnecting && activeEmailProvider === 'zoho'}
                    >
                      {isConnecting && activeEmailProvider === 'zoho' ? (
                        <RefreshCcw className="h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCcw className="h-3 w-3" />
                      )}
                      Sync
                    </Button>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Last synced</span>
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1 text-gray-400" />
                      Just now
                    </span>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  className="w-full mt-2"
                  onClick={() => handleConnectEmail('zoho')}
                  disabled={isConnecting}
                >
                  {isConnecting && activeEmailProvider === 'zoho' ? (
                    <><RefreshCcw className="h-4 w-4 mr-2 animate-spin" /> Connecting...</>
                  ) : (
                    <><Mail className="h-4 w-4 mr-2" /> Connect Zoho Mail</>
                  )}
                </Button>
              )}
            </div>
            
            <div className="space-y-3 mt-4">
              <h3 className="text-sm font-medium">Email Templates</h3>
              <div className="p-3 bg-gray-50 rounded-md text-sm">
                <div className="flex justify-between mb-2">
                  <span className="font-medium">Welcome Message</span>
                  <Button variant="ghost" size="sm" className="h-6 px-2">Edit</Button>
                </div>
                <p className="text-gray-600 text-xs">
                  Hi {"{name}"}, thanks for purchasing Reseller Pro! Your license key is {"{license_key}"}. Let us know if you need any help getting started.
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="forwarding">Auto-Forwarding</Label>
                <Switch id="forwarding" defaultChecked />
              </div>
              <p className="text-xs text-gray-500">
                Forward all customer emails to your primary inbox
              </p>
            </div>
          </CardContent>
          
          <CardFooter className="border-t bg-gray-50 flex flex-col items-start px-6 py-4">
            <h3 className="text-sm font-medium mb-2">Email Integration Benefits</h3>
            <ul className="space-y-1 text-sm">
              <li className="flex text-gray-600">
                <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                <span>Send personalized emails to customers</span>
              </li>
              <li className="flex text-gray-600">
                <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                <span>Track opens, clicks, and replies</span>
              </li>
              <li className="flex text-gray-600">
                <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                <span>Use email templates for faster responses</span>
              </li>
            </ul>
          </CardFooter>
        </Card>
        
        {/* SMS Integration Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="p-2 rounded-full bg-green-100">
                  <MessageSquare className="h-5 w-5 text-green-600" />
                </div>
                <CardTitle>SMS Integration</CardTitle>
              </div>
              
              <Switch 
                checked={isTwilioConnected} 
                onCheckedChange={setIsTwilioConnected}
              />
            </div>
            <CardDescription>
              Send and receive SMS messages through Twilio
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {isTwilioConnected ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-100 rounded-md flex items-center justify-between">
                  <div className="flex items-center">
                    <Check className="h-5 w-5 text-green-600 mr-2" />
                    <div>
                      <p className="font-medium">Connected to Twilio</p>
                      <p className="text-sm text-gray-500">+1 (555) 123-4567</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1">
                    <Settings className="h-4 w-4" />
                    Settings
                  </Button>
                </div>
                
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">Messages Sent (This Month)</p>
                  <div className="font-medium">32</div>
                </div>
                
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">Messages Received (This Month)</p>
                  <div className="font-medium">17</div>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <Label htmlFor="sms-templates">SMS Templates</Label>
                  <div className="p-3 bg-gray-50 rounded-md text-sm">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">Welcome Message</span>
                      <Button variant="ghost" size="sm" className="h-6 px-2">Edit</Button>
                    </div>
                    <p className="text-gray-600 text-xs">
                      Hi {"{name}"}, thanks for purchasing Reseller Pro! Your license key is {"{license_key}"}. Let us know if you need any help getting started.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 border border-gray-100 rounded-md">
                  <p className="text-center text-gray-500 mb-2">Not connected to Twilio</p>
                  <Button className="w-full gap-1">
                    <Phone className="h-4 w-4" />
                    Connect Twilio Account
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">SMS Integration Setup</h3>
                  <ol className="space-y-2 text-sm">
                    <li className="flex">
                      <span className="bg-gray-100 rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 flex-shrink-0">1</span>
                      <span>Create a Twilio account at <a href="#" className="text-primary hover:underline">twilio.com</a></span>
                    </li>
                    <li className="flex">
                      <span className="bg-gray-100 rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 flex-shrink-0">2</span>
                      <span>Purchase a phone number from Twilio</span>
                    </li>
                    <li className="flex">
                      <span className="bg-gray-100 rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 flex-shrink-0">3</span>
                      <span>Get your Account SID and Auth Token</span>
                    </li>
                    <li className="flex">
                      <span className="bg-gray-100 rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 flex-shrink-0">4</span>
                      <span>Enter these details to connect your account</span>
                    </li>
                  </ol>
                </div>
              </div>
            )}
          </CardContent>
          
          <CardFooter className="border-t bg-gray-50 flex flex-col items-start px-6 py-4">
            <h3 className="text-sm font-medium mb-2">Supported SMS Providers</h3>
            <div className="flex space-x-3">
              <Badge provider="twilio" />
              <Badge provider="messagebird" disabled />
              <Badge provider="vonage" disabled />
            </div>
          </CardFooter>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Advanced Integration Settings</CardTitle>
          <CardDescription>
            Configure webhook endpoints and API access
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="webhook-url">Webhook URL</Label>
              <div className="flex gap-2">
                <Input id="webhook-url" value="https://api.referme.site/webhooks/crm" readOnly className="font-mono text-xs" />
                <Button variant="outline" size="sm" className="shrink-0">
                  <LinkIcon className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Use this URL to receive events from integrated services
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="api-key">API Key</Label>
              <div className="flex gap-2">
                <Input id="api-key" type="password" value="sk_live_abcdefghijklmnopqrstuvwxyz" readOnly className="font-mono text-xs" />
                <Button variant="outline" size="sm" className="shrink-0">
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Use this key to authenticate API requests
              </p>
            </div>
          </div>
          
          <Separator />
          
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Available Integrations</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <IntegrationCard 
                name="Zapier"
                description="Connect with 3,000+ apps"
                icon="https://cdn.zapier.com/zapier/images/favicon.ico"
              />
              <IntegrationCard 
                name="Salesforce"
                description="Enterprise CRM integration"
                icon="https://www.salesforce.com/favicon.ico"
                disabled
              />
              <IntegrationCard 
                name="HubSpot"
                description="Marketing automation"
                icon="https://www.hubspot.com/favicon.ico"
                disabled
              />
              <IntegrationCard 
                name="Stripe"
                description="Payment processing"
                icon="https://stripe.com/favicon.ico"
              />
              <IntegrationCard 
                name="Slack"
                description="Team notifications"
                icon="https://slack.com/favicon.ico"
                disabled
              />
              <IntegrationCard 
                name="Calendly"
                description="Appointment scheduling"
                icon="https://calendly.com/favicon.ico"
                disabled
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Badge = ({ provider, disabled = false }: { provider: string, disabled?: boolean }) => {
  const getProviderInfo = (provider: string) => {
    switch (provider) {
      case 'gmail':
        return { 
          name: 'Gmail', 
          bgColor: disabled ? 'bg-gray-100' : 'bg-red-100', 
          textColor: disabled ? 'text-gray-400' : 'text-red-600' 
        };
      case 'outlook':
        return { 
          name: 'Outlook', 
          bgColor: disabled ? 'bg-gray-100' : 'bg-blue-100', 
          textColor: disabled ? 'text-gray-400' : 'text-blue-600' 
        };
      case 'zoho':
        return { 
          name: 'Zoho', 
          bgColor: disabled ? 'bg-gray-100' : 'bg-green-100', 
          textColor: disabled ? 'text-gray-400' : 'text-green-600' 
        };
      case 'twilio':
        return { 
          name: 'Twilio', 
          bgColor: disabled ? 'bg-gray-100' : 'bg-red-100', 
          textColor: disabled ? 'text-gray-400' : 'text-red-600' 
        };
      case 'messagebird':
        return { 
          name: 'MessageBird', 
          bgColor: disabled ? 'bg-gray-100' : 'bg-blue-100', 
          textColor: disabled ? 'text-gray-400' : 'text-blue-600' 
        };
      case 'vonage':
        return { 
          name: 'Vonage', 
          bgColor: disabled ? 'bg-gray-100' : 'bg-purple-100', 
          textColor: disabled ? 'text-gray-400' : 'text-purple-600' 
        };
      default:
        return { 
          name: provider, 
          bgColor: 'bg-gray-100', 
          textColor: 'text-gray-600' 
        };
    }
  };
  
  const { name, bgColor, textColor } = getProviderInfo(provider);
  
  return (
    <span className={`px-2 py-1 rounded text-xs ${bgColor} ${textColor} ${disabled ? 'opacity-60' : ''}`}>
      {name}
      {disabled && <Lock className="inline h-3 w-3 ml-1" />}
    </span>
  );
};

const Eye = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
};

const IntegrationCard = ({ 
  name, 
  description, 
  icon, 
  disabled = false 
}: { 
  name: string; 
  description: string; 
  icon: string; 
  disabled?: boolean;
}) => {
  return (
    <div className={`p-4 rounded-lg border ${disabled ? 'bg-gray-50 opacity-60' : 'bg-white'} flex items-center space-x-3`}>
      <div className="h-10 w-10 rounded overflow-hidden flex-shrink-0">
        <img src={icon} alt={name} className="h-full w-full object-contain" />
      </div>
      <div className="flex-grow">
        <h4 className="font-medium">{name}</h4>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <div>
        <Button variant="ghost" size="sm" disabled={disabled}>
          {disabled ? 'Coming soon' : 'Connect'}
        </Button>
      </div>
    </div>
  );
};

export default IntegrationsPanel;
